import React, { useState } from 'react';
import { useController, useWatch } from 'react-hook-form';

import { useFindSensorGatewayNodeRouterBySerialQuery } from '@components/modals/AddActionItemModal/services/useFindSensorGatewayNodeRouterBySerialQuery';
import Button from '@components/uikit/Button/Button';
import Select from '@components/uikit/Select/Select';
import TreeSelect from '@components/uikit/TreeSelect/TreeSelect';

import useTranslation from '@hooks/useTranslation';

import { useEquipmentsForLocationQuery } from '@services/equipment/useEquipmentsForLocationQuery';
import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useAvailableEquipmentTypesQuery } from '@services/useAvailableEquipmentTypesQuery';
import { useInstPointsByAssetQuery } from '@services/useInstPointsByAssetQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { equipmentTypeLabelList } from '../../constants/equipmentTypeLabelList';
import { ADD_AI_FIELDS } from '../../constants/schemaAddActionItem';
import { checkActiveAI } from '../../helpers/checkActiveAI';
import style from './style.module.scss';

const StepAIPointSelect = ({ control, resetField, setValue, isFilledPoint = false }) => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');

  const locationSelectValue = selectAppLocationSelectValue();

  const { data: assetTreeData } = useAssetTreeQuery();

  const [aiTypeValue, advanceLocationId, advanceEquipment] = useWatch({
    control,
    name: [ADD_AI_FIELDS.TYPE, ADD_AI_FIELDS.ADVANCE_LOCATION_ID, ADD_AI_FIELDS.ADVANCE_EQUIPMENT],
  });

  const {
    field: { value: isAdvancedSearch, onChange: advancedSearchChange },
  } = useController({
    control,
    name: ADD_AI_FIELDS.IS_ADVANCED_SEARCH,
  });

  const {
    field: { value: equipmentType, onChange: equipmentTypeChange },
    fieldState: { error: equipmentTypeError },
  } = useController({
    control,
    name: ADD_AI_FIELDS.EQUIPMENT_TYPE,
  });

  const { data: dataOptions, isFetching: isFetchingDataOptions } =
    useFindSensorGatewayNodeRouterBySerialQuery({
      locationIds:
        locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds[0],
      inputValue: searchValue,
      equipmentType: equipmentType?.id,
    });

  const { data: dataGetForLocation, isFetching: isFetchingGetForLocation } = useEquipmentsForLocationQuery({
    locationId: advanceLocationId,
  });

  const { data: dataGetByAsset, isFetching: isFetchingGetForEquipment } = useInstPointsByAssetQuery({
    assetId: advanceEquipment?.id,
  });

  const { data: equipmentTypesListData, isLoading: isLoadingEquipmentTypesList } =
    useAvailableEquipmentTypesQuery({
      locationIds:
        locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    });

  const currentLocation =
    advanceLocationId && assetTreeData.locations.find((loc) => loc.id === advanceLocationId);

  const getItemLabel = (item) => (
    <div className={style.itemWrapper}>
      {item.label} {!!checkActiveAI(item, aiTypeValue) && <span className={style.disabledMark} />}
    </div>
  );

  const emptySelectData = (searchable) => {
    if (!searchable) {
      return <div className={style.emptySelectData}>{t('No matches were found.')}</div>;
    }

    return (
      <div className={style.emptySelectData}>
        {searchValue ? t('No matches were found.') : t('Enter three or more characters to search')}
      </div>
    );
  };

  const handleChangeAdvancedSearch = () => {
    advancedSearchChange(!isAdvancedSearch);
    resetField(ADD_AI_FIELDS.ADVANCE_LOCATION_ID);
    resetField(ADD_AI_FIELDS.ADVANCE_EQUIPMENT);
    resetField(ADD_AI_FIELDS.INSTALLATION_POINT);
  };

  const handleSimpleSearch = (value) => {
    setSearchValue(value);

    return !!dataOptions?.length && dataOptions;
  };

  return (
    <div className={style.stepAiPointSelect}>
      {aiTypeValue === 'regular' && (
        <>
          <p className={style.stepAiPointSelectDescr}>
            {t(
              'Please select the closest sensor to the area of concern. Waites analysts will review all sensors on the equipment',
            )}
          </p>
          <div className={style.quickSearchBlock}>
            <div className={style.quickSearchLabel}>
              <label className={style.fieldLabel}>
                {t(isAdvancedSearch ? 'Advanced Search' : 'Quick Search')}
              </label>
              {!isFilledPoint && (
                <Button
                  type="secondary"
                  text={t(isAdvancedSearch ? 'Quick Search' : 'Advanced Search')}
                  onClick={handleChangeAdvancedSearch}
                />
              )}
            </div>
            <div className={style.quickSearchWrapper}>
              {!isAdvancedSearch && (
                <div>
                  <Select
                    name={ADD_AI_FIELDS.INSTALLATION_POINT}
                    control={control}
                    label={t(equipmentTypeLabelList[equipmentType?.id || 'sensor'])}
                    searchable={true}
                    options={dataOptions}
                    itemKeys={{ valueFieldKey: 'id', labelFieldKey: 'label', disabledKey: 'isInactive' }}
                    labelRender={(item) => getItemLabel(item)}
                    searchFn={(value) => handleSimpleSearch(value)}
                    disabled={isFilledPoint}
                    loadingList={isFetchingDataOptions}
                    noDataRenderer={() => emptySelectData(true)}
                    required
                  />
                </div>
              )}
              {isAdvancedSearch && (
                <>
                  <TreeSelect
                    items={assetTreeData?.facilities}
                    value={advanceLocationId}
                    onChange={(value) => {
                      setValue(ADD_AI_FIELDS.ADVANCE_LOCATION_ID, value);
                      setValue(ADD_AI_FIELDS.ADVANCE_EQUIPMENT, { id: null });
                      resetField(ADD_AI_FIELDS.INSTALLATION_POINT, { keepDirty: false });
                    }}
                    searchable={true}
                    label={t('Location')}
                    defaultSelectedName={currentLocation?.name || ''}
                    fieldNames={{
                      children: 'children',
                      title: 'name',
                      key: 'id',
                    }}
                    required
                  />
                  <Select
                    value={advanceEquipment}
                    onChange={(value) => {
                      resetField(ADD_AI_FIELDS.INSTALLATION_POINT, { keepDirty: false });
                      setValue(ADD_AI_FIELDS.ADVANCE_EQUIPMENT, value);
                    }}
                    label={t('Equipment')}
                    options={dataGetForLocation || []}
                    itemKeys={{ valueFieldKey: 'id', labelFieldKey: 'name', disabledKey: 'is_deactivate' }}
                    disabled={!advanceLocationId}
                    loadingList={isFetchingGetForLocation}
                    noDataRenderer={() => emptySelectData(false)}
                    required
                  />
                  <Select
                    name={ADD_AI_FIELDS.INSTALLATION_POINT}
                    control={control}
                    label={t('Sensor')}
                    options={dataGetByAsset || []}
                    itemKeys={{ valueFieldKey: 'id', labelFieldKey: 'label', disabledKey: 'isInactive' }}
                    disabled={!advanceEquipment.id}
                    loadingList={isFetchingGetForEquipment}
                    labelRender={(item) => getItemLabel(item)}
                    noDataRenderer={() => emptySelectData(true)}
                    required
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}
      {aiTypeValue === 'waitesWireless' && (
        <div className={style.hardwareFormWrapper}>
          <Select
            value={equipmentType}
            onChange={(value) => {
              equipmentTypeChange(value);
              resetField(ADD_AI_FIELDS.INSTALLATION_POINT, { keepDirty: false });
            }}
            label={t('Equipment Type')}
            options={equipmentTypesListData}
            loadingList={isLoadingEquipmentTypesList}
            disabled={isFilledPoint}
            required
            error={equipmentTypeError}
          />
          {equipmentType?.id && (
            <Select
              name={ADD_AI_FIELDS.INSTALLATION_POINT}
              control={control}
              label={t(equipmentTypeLabelList[equipmentType.id || 'sensor'])}
              searchable={true}
              options={dataOptions}
              itemKeys={{ valueFieldKey: 'id', labelFieldKey: 'label', disabledKey: 'isInactive' }}
              labelRender={(item) => getItemLabel(item)}
              searchFn={(value) => handleSimpleSearch(value)}
              noDataRenderer={({ props: { searchable } }) => emptySelectData(searchable)}
              loadingList={isFetchingDataOptions}
              disabled={isFilledPoint}
              required
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StepAIPointSelect;
